import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-apollo';

import Button from '../../../../../../components/global/Button';
import Angle from '../../../../../../components/global/Angle';
import EditIcon from '../../../../../../components/icons/EditIcon';
import TrashIcon from '../../../../../../components/icons/TrashIcon';
import UploadIcon from '../../../../../../components/icons/UploadIcon';
import Spinner from '../../../../../../components/global/Spinner';
import useAssetImageUpload from '../../../../../../hooks/useAssetImageUpload';
import useRequestConfirm from '../../../../../../hooks/useRequestConfirm';
import { getAsset } from '../../../../../../util/assets';
import { getCustomDesignForType } from '../../../../views/AlbumFolder/AlbumFolderMarketing/helpers';
import useMarketingMaterialAssetSave from './hooks/useMarketingMaterialAssetSave';
import PreviousDesignPicker from './components/PreviousDesignPicker';
import { getAccountMarketingMaterialAssets } from '../../../../../../graphql/queries/marketing-material-assets';
import { downloadReferenceGuide, validateDesignFile } from './helpers';
import {
  CUSTOM_DESIGN_REFERENCE_GUIDE_ASSET,
  CUSTOM_DESIGN_QR_ZONE,
} from './constants';

const propTypes = {
  label: PropTypes.string,
  marketingMaterialType: PropTypes.string,
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }),
  onClose: PropTypes.func,
  onDesignSaved: PropTypes.func.isRequired,
  onDesignRemoved: PropTypes.func.isRequired,
};

const MarketingMaterialAssetConfigurator = ({
  label,
  marketingMaterialType,
  albumFolder,
  onClose,
  onDesignSaved,
  onDesignRemoved,
}) => {
  const [validationError, setValidationError] = useState(null);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [selectedPreviousAsset, setSelectedPreviousAsset] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const requestConfirm = useRequestConfirm();

  const currentDesign = getCustomDesignForType(
    albumFolder,
    marketingMaterialType,
  );
  const { onUpload, onUploadReset, uploading } = useAssetImageUpload({
    onImageUploaded: (fauxImage, assetImage) => {
      setPendingUpload({ ...assetImage, name: fauxImage.original_filename });
    },
  });
  const { onSave, onRemove, submitting } = useMarketingMaterialAssetSave({
    albumFolder,
    marketingMaterialType,
  });
  const { data: { me } = {} } = useQuery(getAccountMarketingMaterialAssets, {
    variables: { marketingMaterialType },
    fetchPolicy: 'cache-and-network',
    skip: !marketingMaterialType,
  });
  const previousDesigns = me?.marketing_material_assets?.records ?? [];

  const previewUrl =
    pendingUpload?.url ??
    selectedPreviousAsset?.asset_image?.url ??
    currentDesign?.marketing_material_asset?.asset_image?.url;
  const hasChanges = !!pendingUpload || !!selectedPreviousAsset;

  const onFilePicked = async event => {
    const [file] = event.target.files;
    // Allow re-picking the same file after a dismiss.
    event.target.value = ''; // eslint-disable-line no-param-reassign

    const error = await validateDesignFile(file);
    setValidationError(error);

    if (!error) {
      setSelectedPreviousAsset(null);
      setPickerOpen(false);
      await onUpload(file);
    }
  };

  const onDismissDesign = () => {
    setValidationError(null);

    if (pendingUpload || selectedPreviousAsset) {
      setPendingUpload(null);
      setSelectedPreviousAsset(null);
      onUploadReset();
      return;
    }

    if (currentDesign) {
      requestConfirm({
        title: 'Remove custom design?',
        message: (
          <p className="text-center">
            This gallery will go back to using the default Waldo design.
          </p>
        ),
        onConfirm: async () => {
          const { success } = await onRemove();

          if (success) {
            onDesignRemoved({ marketingMaterialType });
          }
        },
      });
    }
  };

  const onSubmit = async () => {
    const { success } = await onSave(
      pendingUpload
        ? {
            assetImageId: pendingUpload.id,
            assetName: pendingUpload.name,
          }
        : { existingAssetId: selectedPreviousAsset.id },
    );

    if (success) {
      onDesignSaved({ marketingMaterialType });
    }
  };

  return (
    <div className="marketing-material-asset-configurator">
      {onClose && (
        <Button btnStyle="link" className="mr-2" onClick={onClose}>
          <Angle direction="left" />{' '}
          <span className="d-none d-md-inline-block ml-1">Back</span>
        </Button>
      )}

      <h3 className="text-center">Configure your design</h3>

      <div className="marketing-material-asset-guidelines">
        <div>
          <p className="text-bold mb-1">Guidelines:</p>
          <ol className="pl-3 mb-2">
            <li>
              Ensure your design has an empty section according to the reference
              guide as that is where Waldo&apos;s instructions and QR code will
              be inserted.
            </li>
            <li>
              Upload your design as a <strong>8.5&quot; x 11&quot;</strong> .png
              file.
            </li>
          </ol>
          <Button btnOutline className="px-4" onClick={downloadReferenceGuide}>
            Download reference guide
          </Button>
        </div>
        <div className="marketing-material-asset-guidelines-preview">
          <img
            src={getAsset(CUSTOM_DESIGN_REFERENCE_GUIDE_ASSET)}
            alt={`${label ?? 'Design'} reference guide`}
          />
        </div>
      </div>

      <div className="marketing-material-asset-design-area">
        {uploading && (
          <div className="marketing-material-asset-design-canvas">
            <div className="text-center py-4">
              <Spinner />
            </div>
          </div>
        )}

        {!uploading && previewUrl && (
          <div className="marketing-material-asset-design-configure">
            <p className="marketing-material-asset-design-copy">
              Waldo instructions will be printed in the blue box. Please make
              sure both your design and the instructions will be clear and
              legible.
            </p>
            <div className="marketing-material-asset-design-preview">
              <img src={previewUrl} alt="Your design" />
              <div
                className="marketing-material-asset-design-qr-zone"
                style={CUSTOM_DESIGN_QR_ZONE}
              >
                <span className="text-sm">
                  Waldo&apos;s instructions and QR code will be inserted here
                </span>
              </div>
            </div>
            <div className="marketing-material-asset-design-actions">
              <label
                className="marketing-material-asset-design-action mb-0"
                htmlFor="custom-design-upload"
              >
                <EditIcon size={16} color="#0d84fe" />
              </label>
              <Button
                btnStyle="reset"
                className="marketing-material-asset-design-action marketing-material-asset-design-remove text-danger"
                onClick={onDismissDesign}
              >
                <TrashIcon size={16} color="#fe5851" />
              </Button>
            </div>
          </div>
        )}

        {!uploading && !previewUrl && (
          <div className="marketing-material-asset-design-canvas">
            <div className="marketing-material-asset-design-placeholder">
              <label
                className="btn btn-primary text-transform-none mb-1 w-100"
                htmlFor="custom-design-upload"
              >
                <UploadIcon size={16} color="#fff" className="mr-1" /> Upload
                design
              </label>
              {previousDesigns.length > 0 && (
                <Button btnOutline onClick={() => setPickerOpen(!pickerOpen)}>
                  Use a previous design
                </Button>
              )}
            </div>
          </div>
        )}

        <input
          type="file"
          id="custom-design-upload"
          accept="image/png,.png"
          className="d-none"
          onChange={onFilePicked}
        />

        {validationError && (
          <p className="text-danger text-sm text-center mt-1 mb-0">
            {validationError}
          </p>
        )}

        {pickerOpen && (
          <PreviousDesignPicker
            assets={previousDesigns}
            selectedAssetId={selectedPreviousAsset?.id}
            onSelect={asset => {
              setPendingUpload(null);
              setValidationError(null);
              onUploadReset();
              setSelectedPreviousAsset(asset);
            }}
          />
        )}
      </div>

      <Button
        className="d-block mx-auto px-5 mt-2"
        disabled={!hasChanges || submitting || uploading}
        onClick={onSubmit}
      >
        {submitting ? 'Saving...' : 'Save design and generate'}
      </Button>
    </div>
  );
};

MarketingMaterialAssetConfigurator.propTypes = propTypes;

export default MarketingMaterialAssetConfigurator;
